import { useContext, useEffect, useState } from 'react';

import styled from '@emotion/styled';
import 'react-tooltip/dist/react-tooltip.css';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  AccountActivation,
  BasketPreview,
  CandidateDetails,
  CandidateProductDetails,
  ChangePassword,
  CountrySelector,
  Error401,
  Error404,
  ExamsSearch,
  Inbox,
  LoginError,
  MyProfile,
  MyRegistrations,
  MyRegistrationsPayments,
  MyRegistrationsSummary,
  OfflinePaymentSummary,
  OnlinePaymentSummary,
  PostResultsServices,
  SelectPayment,
  SelectPrsPaymentMethod,
  Signup,
  TermsAndConditions,
} from 'pages';
import { AppSettingsContext, ROUTES, initSentry, includeHeadersInterceptor, includeErrorInterceptor, includeSentryInterceptor } from 'core';
import { CustomSystemTime, Footer, FullScreenLoadingBox, Header } from 'components';

import 'core/styles.scss';
import { useDispatch } from "react-redux";
import axios from "axios";

import { SolasLayout } from '@britishcouncil/react-solas';
import { include401Interceptor, includeAuthInterceptor, OidcContext, RequireAuth, useIsLoggedIn } from '@britishcouncil/react-oidc-auth';
import { setupAuditInterceptor } from "@britishcouncil/react-common";

import { CreatePostResultsService } from './pages/create-post-results-service/CreatePostResultsService';
import { useLazyGetDetailsQuery } from "./store/user/userAPI";
import { clearUser, registrationCreated } from "./store/actions";
import { store } from "./store/store";

export const App = () => {
  const dispatch = useDispatch();
  const { signinRedirect, isAuthenticated } = OidcContext.useAuth();
  const { userManager } = useContext(AppSettingsContext);
  const [interceptorsAdded, setInterceptorsAdded] = useState(false);

  const appSettings = useContext(AppSettingsContext);

  const [triggerDetailsQuery, { isLoading: userDetailsLoading }] = useLazyGetDetailsQuery();

  useEffect(() => {
    includeAuthInterceptor(axios, undefined, userManager);
    include401Interceptor(axios, signinRedirect);
    includeHeadersInterceptor(axios, store);
    includeErrorInterceptor(axios);
    setupAuditInterceptor(axios);
    includeSentryInterceptor(axios);
    setInterceptorsAdded(true);
  }, [signinRedirect]);

  useEffect(() => {
    if (isAuthenticated && interceptorsAdded) {
      triggerDetailsQuery()
    } else {
      dispatch(clearUser());
    }
  }, [dispatch, interceptorsAdded, triggerDetailsQuery, isAuthenticated]);

  useEffect(() => {
    initSentry(appSettings);
  }, [appSettings]);

  if (userDetailsLoading)
    return <FullScreenLoadingBox />;

  return (
    <>
      <SolasLayout id="app-container" className="flex">
        <StyledContainer className="schools-b2c">
          <Header />
          <StyledContents className="app-contents" style={{ flex: 1 }}>
            <Routes>
              <Route path={ROUTES.LOGIN_ERROR} element={<LoginError />} />
              <Route path={ROUTES.ERROR_401} element={<Error401 />} />
              <Route path={ROUTES.SIGN_IN} element={<Signup />} />
              <Route path={ROUTES.BASKET} element={<BasketPreview />} />
              <Route path={ROUTES.ACCOUNT_ACTIVATION} element={<AccountActivation />} />
              <Route
                path={ROUTES.TEST_TAKER_DETAILS}
                element={
                  <RequireAuth>
                    <CandidateDetails />
                  </RequireAuth>
                }
              />

              <Route
                path={ROUTES.CANDIDATE_PRODUCT_DETAILS}
                element={
                  <RequireAuth>
                    <CandidateProductDetails />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.TERMS_AND_CONDITIONS}
                element={
                  <RequireAuth>
                    <TermsAndConditions />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.PAYMENT_ONLINE_SUMMARY}
                element={
                  <RequireAuth>
                    <OnlinePaymentSummary />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.PAYMENT_OFFLINE_SUMMARY}
                element={
                  <RequireAuth>
                    <OfflinePaymentSummary />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.SELECT_PAYMENT}
                element={
                  <RequireAuth>
                    <SelectPayment />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.MY_REGISTRATIONS_PAYMENTS}
                element={
                  <RequireAuth>
                    <MyRegistrationsPayments />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.MY_REGISTRATIONS_SUMMARY}
                element={
                  <RequireAuth>
                    <MyRegistrationsSummary />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.MY_REGISTRATIONS}
                element={
                  <RequireAuth>
                    <MyRegistrations />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.MY_PROFILE}
                element={
                  <RequireAuth>
                    <MyProfile />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.CHANGE_PASSWORD}
                element={
                  <RequireAuth>
                    <ChangePassword />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.INBOX}
                element={
                  <RequireAuth>
                    <Inbox />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.POST_RESULTS_SERVICES}
                element={
                  <RequireAuth>
                    <PostResultsServices />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.CREATE_POST_RESULTS_SERVICE}
                element={
                  <RequireAuth>
                    <CreatePostResultsService />
                  </RequireAuth>
                }
              />
              <Route
                path={ROUTES.SELECT_PAYMENT_METHOD_POST_RESULTS_SERVICES}
                element={
                  <RequireAuth>
                    <SelectPrsPaymentMethod />
                  </RequireAuth>
                }
              />
              <Route path={ROUTES.EXAMS} element={<ExamsSearch />} />
              <Route path={ROUTES.COUNTRY_SELECTOR_WITH_SCHOOL} element={<CountrySelector />} />
              <Route path={ROUTES.COUNTRY_SELECTOR_WITH_ISO_CODE} element={<CountrySelector />} />
              <Route path={ROUTES.COUNTRY_SELECTOR} element={<CountrySelector />} />
              <Route path={ROUTES.ERROR_404} element={<Error404 />} />

              <Route path="/" element={<Navigate to={ROUTES.COUNTRY_SELECTOR} />} />
              <Route path="*" element={<Navigate to={ROUTES.ERROR_404} />} />
            </Routes>
          </StyledContents>
          <Footer />
        </StyledContainer>
      </SolasLayout>
      <CustomSystemTime />
    </>
  );
};

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 100vh;
`;

const StyledContents = styled.div`
    padding-bottom: 30px;
`;
